/**
 * Read live app theme CSS variables for print / export documents.
 * Falls back to the gym CRM green when variables are unavailable.
 */
export function getThemeExportColors() {
  if (typeof window === 'undefined' || !window.getComputedStyle) {
    return {
      primary: '#6bb800',
      primaryMuted: '#e8f5d0',
      text: '#111111',
      textSecondary: '#555555',
      border: '#333333',
      headerBg: '#eeeeee',
      totalBg: '#f3f3f3',
      onPrimary: '#0f1218',
    };
  }

  const styles = window.getComputedStyle(document.documentElement);
  const read = (name, fallback) => {
    const value = styles.getPropertyValue(name).trim();
    return value || fallback;
  };

  const primary = read('--color-primary', '#6bb800');
  return {
    primary,
    primaryMuted: read('--color-primary-muted', '#e8f5d0'),
    text: read('--color-text-primary', '#111111'),
    textSecondary: read('--color-text-secondary', '#555555'),
    border: read('--color-border', '#333333'),
    headerBg: read('--color-primary-muted', '#eeeeee'),
    totalBg: read('--color-primary-muted', '#f3f3f3'),
    onPrimary: read('--color-text-on-primary', '#0f1218'),
  };
}

/**
 * Shared CSS for themed print / PDF HTML.
 * @param {string} extra
 * @param {{ compact?: boolean, fitOnePage?: boolean }} [options]
 */
export function buildThemedPrintCss(extra = '', options = {}) {
  const c = getThemeExportColors();
  const compact = Boolean(options.compact);
  const fitOnePage = Boolean(options.fitOnePage);
  const pad = compact ? '6px' : '18px';
  const cellPad = compact ? '3px 5px' : '6px 8px';
  const fontSize = compact ? '10px' : '12px';
  const brandSize = compact ? '14px' : '18px';
  const subSize = compact ? '12px' : '15px';

  return `
    * { box-sizing: border-box; }
    body {
      font-family: Segoe UI, Tahoma, Geneva, Verdana, sans-serif;
      margin: 0;
      padding: ${pad};
      color: #111;
      background: #fff;
    }
    .theme-header {
      text-align: center;
      background: ${c.primaryMuted};
      border: 1px solid ${c.primary};
      border-bottom: 2px solid ${c.primary};
      padding: ${compact ? '8px 10px' : '14px 16px 12px'};
      margin: 0 0 ${compact ? '8px' : '14px'};
    }
    .theme-brand {
      font-size: ${brandSize};
      font-weight: 800;
      letter-spacing: 0.05em;
      color: ${c.primary};
      text-transform: uppercase;
    }
    .theme-branch {
      font-size: ${compact ? '10px' : '12px'};
      font-weight: 600;
      margin-top: 2px;
      color: ${c.primary};
      text-transform: uppercase;
      opacity: 0.9;
    }
    .theme-title-row {
      display: flex;
      justify-content: center;
      align-items: center;
      margin-top: 4px;
      position: relative;
      font-weight: 800;
    }
    .theme-subtitle {
      font-size: ${subSize};
      font-weight: 800;
      letter-spacing: 0.12em;
      color: ${c.primary};
      text-transform: uppercase;
    }
    .theme-date {
      position: absolute;
      right: 8px;
      font-size: ${compact ? '11px' : '13px'};
      font-weight: 700;
      color: #444;
    }
    .theme-meta {
      text-align: center;
      color: #555;
      font-size: ${compact ? '9px' : '12px'};
      margin: 0 0 ${compact ? '6px' : '16px'};
    }
    h1 { margin: 0 0 4px; font-size: ${compact ? '14px' : '20px'}; text-align: center; color: ${c.primary}; }
    h2 {
      margin: ${compact ? '8px 0 4px' : '22px 0 8px'};
      font-size: ${compact ? '11px' : '15px'};
      border-bottom: 2px solid ${c.primary};
      padding-bottom: 2px;
      color: #111;
    }
    h3 {
      margin: ${compact ? '6px 0 3px' : '14px 0 6px'};
      font-size: ${compact ? '10px' : '13px'};
      color: #111;
      background: ${c.primaryMuted};
      border: 1px solid ${c.primary};
      padding: ${compact ? '3px 6px' : '6px 8px'};
    }
    table { width: 100%; border-collapse: collapse; font-size: ${fontSize}; margin-bottom: ${compact ? '5px' : '10px'}; }
    th, td { border: 1px solid #333; padding: ${cellPad}; vertical-align: middle; color: #111; }
    th {
      background: ${c.primaryMuted};
      color: #111;
      text-align: left;
      font-weight: 800;
    }
    td.num { text-align: right; white-space: nowrap; }
    tr.total td, tr.grand td, .theme-total td {
      background: ${c.primaryMuted};
      font-weight: 700;
    }
    .empty { text-align: center; color: #666; font-style: italic; }
    .sale-page { page-break-after: always; break-after: page; }
    .petty, .petty-page { page-break-before: always; break-before: page; }
    .page-break { page-break-before: always; break-before: page; }
    @page {
      size: A4 portrait;
      margin: ${compact || fitOnePage ? '6mm' : '10mm'};
    }
    @media print {
      body { padding: 0; }
      .theme-header, th, tr.total td, tr.grand td, h3 {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      ${fitOnePage ? `
      html, body { height: auto; }
      .fit-one-page {
        transform-origin: top left;
      }
      ` : ''}
    }
    ${extra}
  `;
}

/** Compact A4 CSS extras for Z Report (Excel-template 2-col layout, theme colors). */
export function buildZReportPrintExtras() {
  const c = getThemeExportColors();
  return `
    body.z-report-print {
      padding: 4mm;
      font-family: Arial, "Segoe UI", Helvetica, sans-serif;
      font-weight: 600;
      color: #000;
      -webkit-font-smoothing: antialiased;
    }
    .zr-banner {
      width: 100%;
      border-collapse: collapse;
      margin: 0 0 8px;
    }
    .zr-banner td {
      background: ${c.primary} !important;
      color: ${c.onPrimary} !important;
      border: 1px solid #222;
      text-align: center;
      font-weight: 800;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      padding: 5px 8px;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .zr-banner .zr-brand { font-size: 14px; font-weight: 800; }
    .zr-banner .zr-branch { font-size: 12px; font-weight: 700; }
    .zr-banner .zr-meta {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 13px;
      font-weight: 800;
      padding: 5px 10px;
    }
    .zr-banner .zr-meta span { color: ${c.onPrimary} !important; font-weight: 800; }
    .zr-pair {
      display: grid;
      grid-template-columns: 1.35fr 12px 1fr;
      gap: 0;
      margin: 0 0 8px;
      align-items: start;
    }
    .zr-pair-gap { width: 12px; min-height: 1px; }
    .zr-pair > table {
      width: 100%;
      margin: 0;
      border-collapse: collapse;
      font-size: 10.5px;
      font-weight: 600;
    }
    .zr-pair th.zr-sec {
      background: ${c.primary} !important;
      color: ${c.onPrimary} !important;
      text-align: center;
      font-weight: 800;
      font-size: 11px;
      letter-spacing: 0.03em;
      text-transform: uppercase;
      padding: 4px 5px;
      border: 1px solid #222;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .zr-pair th.zr-col {
      background: ${c.primaryMuted} !important;
      color: #000 !important;
      text-align: center;
      font-weight: 800;
      font-size: 10px;
      padding: 3px 4px;
      border: 1px solid #333;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .zr-pair td {
      border: 1px solid #333;
      padding: 3px 5px;
      vertical-align: middle;
      font-weight: 600;
      color: #000;
    }
    .zr-pair td.num {
      text-align: right;
      white-space: nowrap;
      font-weight: 700;
    }
    .zr-pair td.dash {
      text-align: center;
      color: #333;
      font-weight: 700;
    }
    .zr-pair tr.total td {
      background: #5a5a5a !important;
      color: #fff !important;
      font-weight: 800;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .theme-header, .theme-meta { display: none !important; }
    .grid-2, .center-rows { display: none !important; }
  `;
}

/** Compact A4 CSS extras for Detailed Report (sales page + petty page). */
export function buildDetailedReportPrintExtras() {
  return `
    .admin-z-sale-amount { text-align: right; }
    .admin-z-sale-header { margin-bottom: 6px; text-align: center; }
    .admin-z-sale-brand { font-size: 14px; font-weight: 800; }
    .admin-z-sale-subtitle-row {
      display: flex; justify-content: space-between; margin-top: 4px; font-weight: 700; font-size: 11px;
    }
    .sale-sections-grid {
      display: grid;
      grid-template-columns: 1fr;
      gap: 8px;
      align-items: start;
      margin-top: 4px;
    }
    .sale-sections-grid > .sale-block,
    .sale-sections-grid > .admin-z-sale-section {
      min-width: 0;
      page-break-inside: avoid;
    }
    .admin-z-sale-section { margin-top: 0; page-break-inside: avoid; }
    .admin-z-sale-section-title {
      font-weight: 800; margin-bottom: 3px; padding: 3px 6px; font-size: 10px;
      border: 1px solid #333;
    }
    .admin-z-sale-table,
    .sale-sections-grid table { font-size: 9px !important; width: 100%; }
    .admin-z-sale-table th, .admin-z-sale-table td,
    .sale-sections-grid th, .sale-sections-grid td { padding: 2px 4px !important; }
    .admin-z-sale-footer-total-row td,
    .admin-z-sale-grand-row td,
    .admin-z-sale-total-row td,
    .admin-z-sale-method-total-row td { font-weight: 700; }
    .admin-z-petty-report, .petty-page { page-break-before: always; break-before: page; margin-top: 0; }
    .sale-page { page-break-after: always; break-after: page; }
    .admin-z-sale-empty { text-align: center; font-style: italic; color: #666; }
    .admin-z-petty-desc { text-align: left; }
    .owner-card { border: none; box-shadow: none; background: #fff; padding: 0; margin: 0; }
    .theme-meta { display: none; }
    h2 { margin: 6px 0 4px; font-size: 11px; }
    h3 { margin: 0 0 2px; font-size: 8px; padding: 2px 4px; border: 1px solid #333; }
    .sale-grand-wrap { margin-top: 6px; grid-column: 1 / -1; }
  `;
}

/**
 * Excel-friendly stylesheet — use with a single flat table and inline cell borders.
 * Nested tables / CSS grid break column alignment in Excel.
 */
export function buildThemedExcelCss(extra = '') {
  const c = getThemeExportColors();
  return `
    body {
      font-family: Arial, Helvetica, sans-serif;
      margin: 0;
      padding: 0;
      color: #111;
      background: #fff;
    }
    table.sheet {
      border-collapse: collapse;
      border-spacing: 0;
      width: 100%;
      font-size: 11px;
      table-layout: fixed;
      mso-table-lspace: 0pt;
      mso-table-rspace: 0pt;
    }
    table.sheet td, table.sheet th {
      border: 1px solid #333;
      padding: 4px 6px;
      vertical-align: middle;
      color: #111;
      mso-style-parent: "";
      white-space: nowrap;
      mso-data-placement: same-cell;
    }
    table.sheet td.gap, table.sheet th.gap {
      border: none !important;
      width: 10px;
      background: #fff;
      padding: 0;
    }
    table.sheet td.blank, table.sheet th.blank {
      border: none !important;
      background: #fff;
      height: 10px;
      padding: 0;
    }
    .hdr {
      background: ${c.primaryMuted};
      color: ${c.primary};
      font-weight: 800;
      text-align: center;
      font-size: 14px;
      text-transform: uppercase;
    }
    .subhdr {
      background: ${c.primaryMuted};
      color: ${c.primary};
      font-weight: 800;
      text-align: center;
      font-size: 12px;
      text-transform: uppercase;
    }
    .sect {
      background: ${c.primaryMuted};
      font-weight: 800;
      text-align: center;
    }
    .tot {
      background: ${c.primaryMuted};
      font-weight: 700;
    }
    .num {
      text-align: right;
      white-space: nowrap;
      mso-number-format: "#,##0";
    }
    .txt { text-align: left; }
    .ctr { text-align: center; }
    ${extra}
  `;
}

/** Escape text for Excel HTML. */
export function excelEscape(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Plain number for Excel cells (no thousand separators — those break column placement). */
export function excelNumber(value) {
  if (value === '' || value == null || value === '—') return '';
  const n = Number(String(value).replace(/,/g, '').trim());
  if (!Number.isFinite(n)) return '';
  return Math.round(n * 100) / 100;
}

/** Build a bordered data cell. */
export function excelTd(content, {
  num = false,
  tot = false,
  center = false,
  colspan = 1,
  rowspan = 1,
  bold = false,
} = {}) {
  const classes = [
    num ? 'num' : (center ? 'ctr' : 'txt'),
    tot ? 'tot' : '',
  ].filter(Boolean).join(' ');
  const span = [
    colspan > 1 ? ` colspan="${colspan}"` : '',
    rowspan > 1 ? ` rowspan="${rowspan}"` : '',
  ].join('');
  const raw = content === 0 ? '0' : (content ?? '');
  const inner = bold ? `<strong>${raw}</strong>` : raw;
  const style = 'border:1px solid #333;padding:4px 6px;';
  return `<td class="${classes}" style="${style}"${span}>${inner}</td>`;
}

/** Section / header cell. */
export function excelTh(content, { colspan = 1, cls = 'sect' } = {}) {
  const span = colspan > 1 ? ` colspan="${colspan}"` : '';
  const style = 'border:1px solid #333;padding:4px 6px;';
  return `<th class="${cls}" style="${style}"${span}>${content}</th>`;
}

/** Empty spacer column (no border). */
export function excelGap() {
  return '<td class="gap" style="border:none;width:10px;padding:0;background:#fff;">&nbsp;</td>';
}

/** Blank full-width spacer row. */
export function excelBlankRow(cols) {
  return `<tr>${Array.from({ length: cols }, () => '<td class="blank" style="border:none;height:4px;padding:0;font-size:1px;line-height:4px;background:#fff;">&nbsp;</td>').join('')}</tr>`;
}

/**
 * Wrap report body HTML for Excel (.xls).
 * Prefer a single <table class="sheet"> in bodyHtml — no nested tables.
 */
export function wrapExcelHtmlDocument({
  title,
  sheetName = 'Report',
  bodyHtml,
  css = '',
  fitWidth = 1,
  fitHeight = 0,
} = {}) {
  const safeSheet = String(sheetName || 'Report').slice(0, 31).replace(/[\\/*?:\[\]]/g, '-');
  const fitH = Number(fitHeight);
  return `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns="http://www.w3.org/TR/REC-html40">
<head>
  <meta charset="utf-8" />
  <meta http-equiv="Content-Type" content="text/html; charset=utf-8" />
  <title>${excelEscape(title)}</title>
  <!--[if gte mso 9]>
  <xml>
    <x:ExcelWorkbook>
      <x:ExcelWorksheets>
        <x:ExcelWorksheet>
          <x:Name>${excelEscape(safeSheet)}</x:Name>
          <x:WorksheetOptions>
            <x:PageSetup>
              <x:Layout x:Orientation="Portrait"/>
              <x:PageMargins x:Bottom="0.4" x:Left="0.35" x:Right="0.35" x:Top="0.4"/>
            </x:PageSetup>
            <x:FitToPage/>
            <x:Print>
              <x:ValidPrinterInfo/>
              <x:PaperSizeIndex>9</x:PaperSizeIndex>
              <x:FitWidth>${Number(fitWidth) || 1}</x:FitWidth>
              <x:FitHeight>${Number.isFinite(fitH) ? fitH : 0}</x:FitHeight>
            </x:Print>
          </x:WorksheetOptions>
        </x:ExcelWorksheet>
      </x:ExcelWorksheets>
    </x:ExcelWorkbook>
  </xml>
  <![endif]-->
  <style>
    ${css || buildThemedExcelCss()}
  </style>
</head>
<body>
${bodyHtml}
</body>
</html>`;
}
